import {
  AgentSourcedCreatorStatus,
  AgentSourcedRunScope,
  IAgentSourcedCreator,
  IAgentSourcedCreatorFilters,
  IAgentSourcedCreatorMeta,
  IAgentSourcedCreatorMetaStats,
  ISourcingRun,
} from '@components/CreatorSearch/types'
import { IHateoasLink } from '@store'

export interface IAgentSourcedCreatorState {
  // BRAND-9550 review fix: which campaign the slice's data — above all its RUN
  // state — currently belongs to. Set from every fetch START (the only action
  // this slice sees that names a campaign), so a fetch for a different campaign
  // is detectable and the run state can be dropped before the request is even
  // built. `null` until the first fetch of the session. Token-mode fetches
  // don't name a campaign and leave this untouched.
  campaignId: number | null
  creators: IAgentSourcedCreator[]
  collectionLinks: IHateoasLink[]
  metadata: IAgentSourcedCreatorMeta | null
  // Which listing the current `creators` came from. Drives the partition
  // selectors for List/Shortlisted so they only count when the loaded
  // creators actually came from an interested fetch.
  listing: AgentSourcedCreatorListing | null
  // Sticky totals per listing. Each updates only from its matching fetch and
  // persists across tab switches — so the sidebar badge for any tab reflects
  // its last known count even when the user is currently viewing a different
  // tab. Vibe-Check is sourced from the /me root link; the two shortlist
  // counts come from the campaign-scoped endpoints.
  vibeCheckTotal: number
  interestedTotal: number
  shortlistedTotal: number
  // BRAND-9678: which campaign the three sticky totals above belong to. They
  // are sticky ACROSS TABS by design, but they were also sticky across
  // BRIEFS, which is a different thing entirely: opening brief B rendered
  // brief A's Vibe Check badge until a real vibe-check list fetch replaced it
  // (i.e. until the user clicked into the tab). Tracked separately from
  // `campaignId` above because that field's transition is what drops the RUN
  // state, and it is only written by the list fetch — the counts warm-up
  // names a campaign too, and on the List/Shortlisted tabs it is the ONLY
  // thing that does. `null` until the first fetch of the session.
  totalsCampaignId: number | null
  loading: boolean
  error: string | null
  actionLoading: Record<number, boolean>
  // BRAND-9306 run-aware review views. `selectedRunScope` drives the
  // `?sourcing_run=` param the fetch saga appends; `null` (the default)
  // means "omitted" (BE resolves to the latest run). Contract is
  // provisional pending BRAND-9510 (BE) — re-verify once that PR merges.
  selectedRunScope: AgentSourcedRunScope
  // BRAND-9550: set by startDiscoveryRunSuccess, consumed by the next fetch
  // that carries `metadata.sourcing_runs`. A just-started run is `importing`,
  // and the default-selection contract now prefers the newest COMPLETED run
  // (mirroring the BE), so "land on the run I just started" can no longer be
  // a side effect of clearing the scope to null — it has to be selected
  // explicitly once the new run actually appears in a response.
  //
  // Holds the CAMPAIGN the run was started on rather than a bare boolean
  // (review fix): the consuming response has to be that campaign's, or a
  // campaign the user started nothing on gets pinned to an ordinal it never
  // chose. `null` = nothing pending.
  pendingNewRunCampaignId: number | null
  // Sticky summary of the campaign's sourcing runs, populated from
  // metadata.sourcing_runs on the first page of a fresh fetch (mirrors how
  // `stats` populates the bucket totals — see fetchAgentSourcedCreatorsSuccess).
  sourcingRuns: ISourcingRun[]
  // BRAND-9514 bulk reject: request status only — the selection itself is
  // local component state in AgentSourcedCreatorListView. The view exits
  // selection mode on the pending -> succeeded transition and keeps the
  // selection (modal dismissed) on pending -> failed, so the user can adjust
  // after an all-or-nothing 422.
  bulkRejectStatus: BulkRejectStatus
}

export type BulkRejectStatus = 'idle' | 'pending' | 'succeeded' | 'failed'

// Which BE listing the fetch saga should target. Defaults to 'vibe-check'
// (existing behavior). 'interested' returns all creators with approved intent
// (shortlisted or not); 'shortlisted' returns already-shortlisted candidates.
// Both brand-manager endpoints introduced in TRIBE-Platform PR #5155 / #5172.
export type AgentSourcedCreatorListing = 'vibe-check' | 'interested' | 'shortlisted'

export interface IFetchAgentSourcedCreatorsPayload {
  campaignId: number
  filters?: IAgentSourcedCreatorFilters
  // When set, hit this URL directly (used for HATEOAS next-page navigation).
  // When omitted, the saga resolves the URL from /me and appends current filter
  // params (fresh-load semantics).
  urlOverride?: { href: string; method: string }
  // When true, the saga forwards `append: true` to the success action so the
  // reducer concats results instead of replacing them (infinite scroll).
  append?: boolean
  // Selects which listing endpoint to hit. Defaults to 'vibe-check' when absent.
  listing?: AgentSourcedCreatorListing
}

export interface IAgentSourcedCreatorActionPayload {
  creatorId: number
  campaignId: number
  href: string
  method: string
}

export interface IAgentSourcedCreatorActionSuccessPayload {
  creatorId: number
  status: AgentSourcedCreatorStatus
  updatedLinks: IHateoasLink[]
  // When set, the reducer also applies the new `shortlisted` value to the
  // matched creator. Used by shortlist/unshortlist actions where the BE
  // returns `shortlisted: true`/`false` alongside refreshed mutation links.
  shortlisted?: boolean
}

// BRAND-9514/9550: PUT to the COLLECTION-level bulk_reject link. All-or-nothing
// on the BE — a 422 names the invalid ids and changes nothing. `comment` is an
// optional shared note; omitted from the body entirely when blank.
interface IBulkRejectCommonPayload {
  campaignId: number
  comment?: string
  // The view's active filters at confirm time (adversarial finding 1): the
  // saga forwards these to the success refetch so a filtered list reloads
  // FILTERED, matching what the filter chrome still shows. Run scope needs no
  // equivalent for the REFETCH — fetchCreatorsSaga selects it fresh from redux
  // itself (the select-all form below carries it for a different reason: it
  // has to travel to the BE as part of the request).
  filters?: IAgentSourcedCreatorFilters
  href: string
  method: string
}

// Form 1 (BRAND-9514): an explicit id list — everything the user literally
// ticked on the loaded pages.
export interface IBulkRejectByIdsPayload extends IBulkRejectCommonPayload {
  ids: number[]
}

// The run a destructive select-all binds to. Deliberately EXCLUDES the `null`
// that `AgentSourcedRunScope` allows for the LIST query, because the two mean
// different things on a read and on a write:
//
//   read  — omit `?sourcing_run=`, BE resolves the default run, you get a page
//   write — omit `sourcing_run`,   BE resolves the default run AT WRITE TIME
//
// and "the default run" is not a fixed thing. `default_run_ids` picks the
// newest ready run as of the write, so a rerun that finished importing while
// the tab sat open silently moves the target: the AM confirms on Run 1's
// creators and Run 2's get rejected instead (BRAND-9550 defect). The run is
// therefore bound on the CLIENT at confirm time — see
// AgentSourcedCreatorListView's `bulkRejectRunScope` — and narrowing the type
// here is what makes the unbound form unrepresentable rather than a comment
// nobody reads. `'all'` stays: it is an explicit scope, not an absence.
export type BulkRejectRunScope = Exclude<AgentSourcedRunScope, null>

// Form 2 (BRAND-9550): a SCOPE FLAG. The list is BE-paginated, so an id list
// can only ever name the loaded page; this hands the BE the same scope the
// user is looking at and lets it resolve the pending set server-side.
export interface IBulkRejectAllPendingPayload extends IBulkRejectCommonPayload {
  allPending: true
  // The CONCRETE run the user is looking at — an ordinal, or 'all'. Never an
  // absence (see BulkRejectRunScope).
  sourcingRun: BulkRejectRunScope
}

// The two forms are MUTUALLY EXCLUSIVE on the wire — sending both or neither
// is a 422 (`invalid_bulk_reject_ids`). The union makes that unrepresentable
// on the FE side rather than a runtime hope.
export type IBulkRejectAgentSourcedCreatorsPayload =
  | IBulkRejectByIdsPayload
  | IBulkRejectAllPendingPayload

export const isBulkRejectAllPending = (
  payload: IBulkRejectAgentSourcedCreatorsPayload,
): payload is IBulkRejectAllPendingPayload => 'allPending' in payload

export interface IFetchAgentSourcedCreatorsCountPayload {
  campaignId: number
  listing: AgentSourcedCreatorListing
}

export interface IFetchAgentSourcedCreatorsCountSuccessPayload {
  listing: AgentSourcedCreatorListing
  total: number
  // Which campaign this count is FOR (BRAND-9678). The reducer drops a
  // response whose campaign is no longer the one the totals belong to —
  // otherwise a warm-up still in flight when the user switches briefs lands
  // brief A's total on brief B, which is the same stale-badge symptom by a
  // narrower route.
  campaignId: number
  // The unfiltered all-bucket totals, when the response carried them. Every
  // one of these endpoints returns `metadata.stats` on its first page (and a
  // `per_page=1` warm-up IS a first page), so one warm-up refreshes all three
  // badges — including vibe-check's, whose own `metadata.total` is run-scoped
  // and must never be used for it (see the reducer).
  stats?: IAgentSourcedCreatorMetaStats
}
